import React, {memo, useCallback} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import {AppButton, AppButtonVariant} from '@/app/shared-components';

import {KID_DOWNLOAD_APP_URL} from '../onboarding/pages/DownloadApp/models/constants';

import {getEditInfoImage, redirectToEditInfoApp} from './constants';
import EditInfoLayout from './EditInfoLayout';
import {useEditInfoTrackers} from './useEditInfoTrackers';

function EditInfoSuccessContent() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const parentId: string = location.state?.parentId ?? '';
  const teenName: string = location.state?.teenName ?? '';
  const switchAccount: boolean = location.state?.switchAccount ?? false;
  const isMoGold: boolean = location.state?.isMoGold ?? false;

  const {
    trackSelectEditInfoInviteKid,
    trackShareEditInfoInviteKidSuccess,
    trackSelectEditInfoBackToApp,
  } = useEditInfoTrackers({
    parentId,
  });

  const title = teenName
    ? t('edit_info.success.title', {name: teenName})
    : t('edit_info.success.title_generic');

  const primaryButtonLabel = teenName
    ? t('edit_info.success.primary_button', {name: teenName})
    : t('edit_info.success.primary_button_generic');

  const handleInvite = useCallback(async () => {
    trackSelectEditInfoInviteKid();
    try {
      if (!navigator.share) {
        throw new Error('Web Share API not supported');
      }

      const shareTitle = t('edit_info.success.share_title');

      const shareText = t('edit_info.success.share_text', {
        name: teenName ?? '',
        link: KID_DOWNLOAD_APP_URL,
        interpolation: {escapeValue: false},
      });

      await navigator.share({
        title: shareTitle,
        text: shareText,
      });
      trackShareEditInfoInviteKidSuccess();
      return;
    } catch (error) {
      navigate('/onboarding/download-app');
    }
  }, [
    navigate,
    t,
    teenName,
    trackSelectEditInfoInviteKid,
    trackShareEditInfoInviteKidSuccess,
  ]);

  const handleGoBackToApp = useCallback(() => {
    trackSelectEditInfoBackToApp();
    redirectToEditInfoApp();
  }, [trackSelectEditInfoBackToApp]);

  return (
    <EditInfoLayout
      illustrationSrc={getEditInfoImage('moWithPhone', isMoGold)}
      title={title}
      description={t('edit_info.success.description')}
      disclaimer={t('edit_info.success.disclaimer')}
      buttons={
        <>
          {switchAccount && (
            <AppButton
              id="EditInfoSuccess_inviteButton"
              sentry-label="EditInfoSuccess invite button"
              label={primaryButtonLabel}
              onClick={handleInvite}
              variant={
                isMoGold
                  ? AppButtonVariant.onPrimary
                  : AppButtonVariant.onPrimaryGreen
              }
            />
          )}
          <AppButton
            id="EditInfoSuccess_goBackToAppButton"
            sentry-label="EditInfoSuccess go back to app button"
            label={t('edit_info.success.secondary_button')}
            onClick={handleGoBackToApp}
            variant={
              isMoGold
                ? AppButtonVariant.onQuinary
                : AppButtonVariant.onTertiaryGreen
            }
          />
        </>
      }
    />
  );
}

const EditInfoSuccessPage = memo(() => <EditInfoSuccessContent />);

export default EditInfoSuccessPage;
